import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { http } from "@/lib/api";
import { ArrowUpRight, Camera } from "@phosphor-icons/react";

// PhotoPulseStrip — a horizontal preview of the curated NaturePulse-tagged photos
// living on PhotoPulse. Failing silently: if PhotoPulse is unavailable, we render
// nothing (the strip is optional; sighting submissions are not).

export default function PhotoPulseStrip({ limit = 8 }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    http.get(`/photopulse/feed?limit=${limit}`)
      .then((r) => {
        if (!alive) return;
        setPhotos(r.data?.photos || []);
      })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [limit]);

  if (error || (!loading && photos.length === 0)) return null;

  return (
    <section data-testid="photopulse-strip" className="bg-[#0A1620] text-[color:var(--np-cream,#F5EBDC)] py-14 md:py-20 border-t border-[#F5EBDC]/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[#E4A15C] mb-2">
              /04 · From PhotoPulse
            </div>
            <h2 className="font-serif-display text-3xl md:text-5xl leading-none tracking-tighter">
              Curated by the community.
            </h2>
          </div>
          <Link
            to="/photopulse"
            data-testid="photopulse-strip-more"
            className="hidden md:inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/75 hover:text-[#F5EBDC]"
          >
            See full gallery <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {(loading ? Array.from({ length: limit }) : photos).map((p, i) => (
            <motion.a
              key={p?.id ?? i}
              href={p?.detail_url || p?.image_url || "#"}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              data-testid={`photopulse-strip-card-${i}`}
              className="group block relative overflow-hidden border border-[#F5EBDC]/10 aspect-square bg-[#101E2A]"
            >
              {p?.image_url ? (
                <img
                  src={p.image_url}
                  alt={p.title || p.species || "PhotoPulse photo"}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#E5D8C4]/30">
                  <Camera size={26} weight="thin" />
                </div>
              )}
              {p && (
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                  <div className="font-serif-display text-sm leading-tight text-[#F5EBDC] line-clamp-2">
                    {p.title || p.species || "Untitled"}
                  </div>
                  {p.photographer && (
                    <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#E5D8C4]/70 line-clamp-1">
                      by {p.photographer}
                    </div>
                  )}
                </div>
              )}
            </motion.a>
          ))}
        </div>

        <div className="mt-6 md:hidden">
          <Link to="/photopulse" data-testid="photopulse-strip-more-mobile" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E5D8C4]/75">
            See full gallery <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}
